import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Rating,
  Chip,
} from '@mui/material';
import { Search, ShoppingCart, Favorite } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_name: string;
  stock_quantity: number;
}

interface Category {
  id: number;
  name: string;
}

const Shop: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const productsPerPage = 12;
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://salon.rockyroadsolutions.com/api/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setTotalPages(Math.ceil(data.length / productsPerPage));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleAddToCart = async (productId: number) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/cart/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: 1,
        }),
      });

      if (response.ok) {
        // Show success message or update cart count
        alert('Product added to cart!');
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        alert(errorData.error || 'Failed to add product to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding product to cart');
    }
  };

  const handleAddToWishlist = async (productId: number) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/wishlist/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
        }),
      });

      if (response.ok) {
        alert('Product added to wishlist!');
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/shop/product/${productId}`);
  };

  // Filter products based on search and category
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? product.category_name === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  // Paginate products
  const paginatedProducts = filteredProducts.slice(
    (page - 1) * productsPerPage,
    page * productsPerPage
  );

  return (
    <Box>
      <Box sx={{ 
        background: 'linear-gradient(135deg, #E91E63 0%, #880E4F 100%)',
        color: 'white',
        p: { xs: 4, md: 6 },
        borderRadius: 3,
        mb: 5,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(233, 30, 99, 0.3)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'url(https://images.unsplash.com/photo-1571781926291-c477ebfd024b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80) center/cover no-repeat',
          opacity: 0.3,
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '30%',
          background: 'linear-gradient(to top, rgba(173, 20, 87, 0.7), transparent)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h2" 
            fontWeight={700} 
            gutterBottom
            sx={{ 
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              letterSpacing: '1px',
              fontFamily: '"Playfair Display", serif'
            }}
          >
            Bella Vista Shop
          </Typography>
          <Typography 
            variant="h5"
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto', 
              mb: 3,
              fontWeight: 500,
              letterSpacing: '0.5px',
              color: 'white',
              textShadow: '1px 1px 3px rgba(0,0,0,0.5)'
            }}
          >
            Professional Beauty Products for Home Care
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '600px', 
              mx: 'auto', 
              fontWeight: 500, 
              backgroundColor: 'rgba(255,255,255,0.85)', 
              color: '#333',
              p: 2,
              borderRadius: 1,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
          >
            Discover salon-quality products to maintain your beauty routine at home. 
            Handpicked by our professional stylists for exceptional results.
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 2 }}
        />
        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={handleCategoryChange}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Products Grid */}
      {loading ? (
        <Typography>Loading products...</Typography>
      ) : paginatedProducts.length === 0 ? (
        <Typography>No products found.</Typography>
      ) : (
        <Grid container spacing={3}>
          {paginatedProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image_url || 'https://via.placeholder.com/300x200?text=Product+Image'}
                  alt={product.name}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleProductClick(product.id)}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Chip 
                      label={product.category_name} 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#E91E63', 
                        color: 'white',
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      fontWeight: 600, 
                      cursor: 'pointer',
                      '&:hover': { color: '#E91E63' }
                    }}
                    onClick={() => handleProductClick(product.id)}
                  >
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                    {product.description}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="#E91E63" fontWeight={700}>
                      ${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}
                    </Typography>
                    <Rating value={4} readOnly size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant="contained" 
                      startIcon={<ShoppingCart />}
                      onClick={() => handleAddToCart(product.id)}
                      fullWidth
                      sx={{ 
                        background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #AD1457 0%, #880E4F 100%)',
                        }
                      }}
                    >
                      Add to Cart
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={() => handleAddToWishlist(product.id)}
                      sx={{ minWidth: 'auto', color: '#E91E63', borderColor: '#E91E63' }}
                    >
                      <Favorite />
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {filteredProducts.length > productsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={Math.ceil(filteredProducts.length / productsPerPage)} 
            page={page} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>
      )}
    </Box>
  );
};

export default Shop;
